import { useCallback, useEffect, useState } from 'react';

import { CellTypeNames, TextAlignment } from '../../../../common/table/CellTypeConstants';
import type { CellData, CellEditingSession } from '../../../../common/table/TableTypes';

import { getCellEditingAlignment, getCellTextAlignment } from '../utils/dataUtils/cell/tableCellDisplayUtils';
import { isFormula } from '../utils/dataUtils/formula/tableFormulaPredicateUtils';

type UseTableCellTextAlignmentArgs = {
    cellData: CellData | undefined;
    cellEditingSession: CellEditingSession | null;
    locale: string;
};

type UseTableCellTextAlignmentReturn = {
    textAlignment: TextAlignment | null;
    recomputeEditingTextAlignment: (editCellValue: string) => void;
};

/**
 * Tracks the live text alignment shown by a table cell editor during an edit session.
 */
const useTableCellTextAlignment = ({
    cellData,
    cellEditingSession,
    locale,
}: UseTableCellTextAlignmentArgs): UseTableCellTextAlignmentReturn => {
    const [textAlignment, setTextAlignment] = useState<TextAlignment | null>(null);

    useEffect(() => {
        if (!cellEditingSession || !cellData) return;

        // For the editor only, ensure date types stay right aligned while editing.
        const isDateTimeString = !isFormula(cellData.value) && cellData.type?.name === CellTypeNames.DATE_TIME;
        const editorTextAlignment = isDateTimeString ? TextAlignment.RIGHT : getCellTextAlignment(cellData, locale);

        setTextAlignment(editorTextAlignment);
    }, [cellData, cellEditingSession, locale]);

    const recomputeEditingTextAlignment = useCallback(
        (editCellValue: string) => {
            if (!cellEditingSession || !cellData) return;

            const updatedAlignment = getCellEditingAlignment(editCellValue, cellData, locale);
            if (updatedAlignment) setTextAlignment(updatedAlignment);
        },
        [cellData, cellEditingSession, locale],
    );

    return {
        textAlignment,
        recomputeEditingTextAlignment,
    };
};

export default useTableCellTextAlignment;
